'use client'

import Image from 'next/image'
import type { CSSProperties } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'

// A chip is either a plain label or a label with a square thumbnail. Rows can
// mix the two, but a page should pick one style per ticker.
export type ContinuousChipTickerItem =
  | string
  | { label: string; image: string; alt?: string }

type ContinuousChipTickerProps = {
  rows: ContinuousChipTickerItem[][]
  directions?: Array<'left' | 'right'>
  /** Fades the row edges into the page background. */
  edgeFade?: boolean
  className?: string
}

function itemLabel(item: ContinuousChipTickerItem) {
  return typeof item === 'string' ? item : item.label
}

const defaultCopiesPerSegment = 8

export function ContinuousChipTicker({
  rows,
  directions = ['left', 'right'],
  edgeFade = false,
  className = 'gap-2.5 lg:gap-3',
}: ContinuousChipTickerProps) {
  return (
    <div className={`relative flex flex-col ${className}`}>
      {rows.map((items, rowIndex) => (
        <ContinuousChipTickerRow
          key={`${rowIndex}-${items.map(itemLabel).join('-')}`}
          items={items}
          direction={
            directions[rowIndex] ?? (rowIndex % 2 === 0 ? 'left' : 'right')
          }
        />
      ))}
      {edgeFade ? (
        <>
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 left-0 z-10 w-[48px] bg-gradient-to-r from-white to-transparent xl:w-[160px]"
          />
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-0 z-10 w-[48px] bg-gradient-to-l from-white to-transparent xl:w-[160px]"
          />
        </>
      ) : null}
    </div>
  )
}

function ContinuousChipTickerRow({
  items,
  direction,
}: {
  items: ContinuousChipTickerItem[]
  direction: 'left' | 'right'
}) {
  const viewportRef = useRef<HTMLDivElement>(null)
  const measurementRef = useRef<HTMLDivElement>(null)
  const [copiesPerSegment, setCopiesPerSegment] = useState(
    defaultCopiesPerSegment,
  )
  const [segmentWidth, setSegmentWidth] = useState(0)

  useEffect(() => {
    const viewport = viewportRef.current
    const measurement = measurementRef.current

    if (!viewport || !measurement || items.length === 0) return

    const updateCopies = () => {
      const viewportWidth = viewport.getBoundingClientRect().width
      const itemSetWidth = measurement.getBoundingClientRect().width

      if (viewportWidth === 0 || itemSetWidth === 0) return

      const nextCopies = Math.max(
        2,
        Math.ceil((viewportWidth + itemSetWidth) / itemSetWidth),
      )

      setCopiesPerSegment((current) =>
        current === nextCopies ? current : nextCopies,
      )
      setSegmentWidth(itemSetWidth * nextCopies)
    }

    updateCopies()

    const resizeObserver = new ResizeObserver(updateCopies)
    resizeObserver.observe(viewport)
    resizeObserver.observe(measurement)

    return () => resizeObserver.disconnect()
  }, [items])

  const segmentCopies = useMemo(
    () => Array.from({ length: copiesPerSegment }, (_, index) => index),
    [copiesPerSegment],
  )
  const pixelsPerSecond = direction === 'left' ? 38 : 34
  const duration = Math.max(24, segmentWidth / pixelsPerSecond)
  const trackStyle = {
    '--continuous-chip-ticker-duration': `${duration}s`,
  } as CSSProperties

  if (items.length === 0) return null

  return (
    <div
      ref={viewportRef}
      className="continuous-chip-ticker relative overflow-hidden"
    >
      <span className="sr-only">{items.map(itemLabel).join(', ')}</span>
      <div
        ref={measurementRef}
        aria-hidden="true"
        className="pointer-events-none invisible absolute flex w-max shrink-0 gap-2.5 pr-2.5 lg:gap-3 lg:pr-3"
      >
        {items.map((item) => (
          <TickerChip key={itemLabel(item)} item={item} />
        ))}
      </div>
      <div
        aria-hidden="true"
        className={`continuous-chip-ticker__track continuous-chip-ticker__track--${direction} flex w-max will-change-transform`}
        style={trackStyle}
      >
        {[0, 1].map((segmentIndex) => (
          <div
            key={segmentIndex}
            className="continuous-chip-ticker__segment flex shrink-0"
          >
            {segmentCopies.map((copyIndex) => (
              <div
                key={copyIndex}
                className="flex shrink-0 gap-2.5 pr-2.5 lg:gap-3 lg:pr-3"
              >
                {items.map((item) => (
                  <TickerChip
                    key={`${copyIndex}-${itemLabel(item)}`}
                    item={item}
                  />
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

function TickerChip({ item }: { item: ContinuousChipTickerItem }) {
  if (typeof item === 'string') {
    return (
      <span className="shrink-0 rounded-full bg-[#F4F4F6] px-5 py-[11px] text-sm font-semibold leading-[18px] lg:px-[26px] lg:py-[13px] lg:text-[15px]">
        {item}
      </span>
    )
  }

  return (
    <span className="flex h-16 shrink-0 items-center gap-3 rounded-[20px] bg-[#F6F6F8] py-2.5 pl-2.5 pr-5 text-[15px] font-semibold leading-5 tracking-[-0.01em] xl:h-[92px] xl:gap-4 xl:rounded-[26px] xl:py-3 xl:pl-3 xl:pr-7 xl:text-[19px] xl:leading-6">
      <span className="relative h-11 w-11 shrink-0 overflow-hidden rounded-xl xl:h-[68px] xl:w-[68px] xl:rounded-2xl">
        <Image
          src={item.image}
          alt={item.alt ?? ''}
          fill
          sizes="68px"
          className="object-cover"
        />
      </span>
      {item.label}
    </span>
  )
}
